/** @format */

import apis from "../../utils/apis";

// ── Async polling configuration (matches CVScore pattern) ──────────────────
const MAX_RETRIES = 2;
const MAX_POLL_TRIES = 10;
const INITIAL_POLL_DELAY_MS = 30000;
const POLL_INTERVAL_MS = 30000;
export const MAX_STAGE_ATTEMPTS = 3;

/**
 * Generic retry wrapper for API calls.
 * Retries on 504 (gateway timeout) with exponential backoff.
 */
export async function retryApi(apiCall, maxRetries = MAX_RETRIES) {
  let retries = 0;
  while (retries <= maxRetries) {
    try {
      const res = await apiCall();
      if (res.status === 200 || res.status === 201 || res.status === 202)
        return res;
      throw new Error("Unexpected status code");
    } catch (err) {
      const status = err?.response?.status ?? err?.status;
      const isRetryable = status === 504;
      if (isRetryable && retries < maxRetries) {
        retries++;
        await new Promise((r) => setTimeout(r, 1000 * Math.pow(2, retries)));
      } else throw err;
    }
  }
}

/**
 * Wrapper for apis.GET that throws errors instead of returning them.
 * The base apis.GET returns errors instead of throwing;
 * this ensures proper error propagation for the polling logic.
 */
async function apiGet(config) {
  const res = await apis.GET(config);
  if (res instanceof Error || !res?.status) {
    throw res instanceof Error ? res : new Error("GET request failed");
  }
  return res;
}

/**
 * Generic status-poller for async endpoints.
 * Waits an initial delay, then polls at a fixed interval until the stage
 * reports "completed", "failed", or the attempt budget is exhausted.
 */
export async function pollUntilTerminal({
  statusEndpoint,
  statusField,
  completedValue = "completed",
  failedValue = "failed",
  timeoutError,
}) {
  let pollDelay = INITIAL_POLL_DELAY_MS;
  for (let i = 0; i < MAX_POLL_TRIES; i++) {
    await new Promise((resolve) => setTimeout(resolve, pollDelay));

    const statusRes = await retryApi(() =>
      apiGet({ endpoint: statusEndpoint })
    );

    const data = statusRes?.data ?? {};
    const currentStatus = data?.[statusField];

    if (currentStatus === failedValue) {
      throw new Error(
        data?.error || timeoutError || "Background processing failed."
      );
    }

    if (currentStatus === completedValue) return data;

    pollDelay = POLL_INTERVAL_MS;
  }

  return null; // timed out without terminal status
}
